/**
 * Folder Upload Component
 *
 * Allows users to select a folder containing PDF facesheets.
 * Validates folder name matches pattern: PracticeName_Facesheets
 * Uploads all PDFs in batch to the active session.
 *
 * Desktop equivalent: QFileDialog.getExistingDirectory + _load_pdfs_from_folder
 */

import { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Paper,
  Alert,
  AlertTitle,
} from '@mui/material';
import { FolderOpen, Upload } from '@mui/icons-material';
import { pdfsApi } from '../../api/pdfs';
import { useSessionStore } from '../../store/sessionStore';
import { useSnackbar } from '../../contexts/SnackbarContext';

export const FolderUpload: React.FC = () => {
  const { activeSession, customerId, practiceId } = useSessionStore();
  const { showSuccess, showError, showWarning, showInfo } = useSnackbar();

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [folderName, setFolderName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationError, setValidationError] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Validate folder name follows pattern: PracticeName_Facesheets
   */
  const validateFolderName = (name: string): boolean => {
    // Check if name ends with _Facesheets
    if (!name.endsWith('_Facesheets')) {
      setValidationError(
        'Folder name must end with "_Facesheets". Example: "CityBilling_Facesheets"'
      );
      return false;
    }

    // Check if there's a practice name before _Facesheets
    const practiceName = name.replace('_Facesheets', '');
    if (!practiceName || practiceName.length < 2) {
      setValidationError(
        'Folder name must include practice name. Example: "CityBilling_Facesheets"'
      );
      return false;
    }

    setValidationError('');
    return true;
  };

  /**
   * Handle folder selection
   * Note: Web browsers don't have native folder selection with name access.
   * We use directory input which gives us files and infer folder name from path.
   */
  const handleFolderSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    // Convert FileList to Array and filter PDFs only
    const pdfFiles = Array.from(files).filter(
      (file) => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    );

    // Filter out macOS hidden files (starting with ._ )
    const validPdfFiles = pdfFiles.filter((file) => !file.name.startsWith('._'));

    if (validPdfFiles.length === 0) {
      showWarning('No PDF files found in selected folder');
      return;
    }

    // Extract folder name from first file's webkitRelativePath
    // Format: "FolderName/file.pdf"
    const firstFile = validPdfFiles[0] as File & { webkitRelativePath?: string };
    const relativePath = firstFile.webkitRelativePath || firstFile.name;
    const extractedFolderName = relativePath.split('/')[0] || 'Unknown';

    setFolderName(extractedFolderName);

    // Validate folder name
    if (!validateFolderName(extractedFolderName)) {
      setSelectedFiles([]);
      return;
    }

    setSelectedFiles(validPdfFiles);
    showInfo(`Found ${validPdfFiles.length} PDF file${validPdfFiles.length !== 1 ? 's' : ''} in "${extractedFolderName}"`);
  };

  /**
   * Upload all selected PDFs to session
   */
  const handleUpload = async () => {
    if (!activeSession || selectedFiles.length === 0) {
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    let successCount = 0;
    let failureCount = 0;

    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];

        try {
          // Upload PDF
          const uploadResult = await pdfsApi.uploadPDF(
            activeSession.session_id,
            practiceId,
            file
          );

          // Auto-trigger extraction after upload (matches desktop behavior)
          await pdfsApi.extractPDF(uploadResult.pdf_id, customerId);

          successCount++;
        } catch (error: any) {
          console.error(`Failed to upload ${file.name}:`, error);
          failureCount++;
        }

        // Update progress
        setUploadProgress(((i + 1) / selectedFiles.length) * 100);
      }

      // Show summary
      if (failureCount === 0) {
        showSuccess(`Successfully uploaded and extracted ${successCount} PDF${successCount !== 1 ? 's' : ''}`);
      } else {
        showWarning(
          `Uploaded ${successCount} PDF${successCount !== 1 ? 's' : ''}, ${failureCount} failed`
        );
      }

      // Clear selection after successful upload
      setSelectedFiles([]);
      setFolderName('');
      setUploadProgress(0);
    } catch (error: any) {
      showError('Upload process failed: ' + (error.message || 'Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  /**
   * Open folder picker dialog
   */
  const handleSelectClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Upload Facesheet PDFs
      </Typography>

      {/* Important instruction */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>Folder Naming Requirement</AlertTitle>
        Your folder must be named following this pattern:{' '}
        <strong>PracticeName_Facesheets</strong>
        <br />
        Example: <strong>CityBilling_Facesheets</strong>
      </Alert>

      {/* Validation error */}
      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationError}
        </Alert>
      )}

      {/* Hidden file input with directory attribute */}
      <input
        ref={fileInputRef}
        type="file"
        /* @ts-ignore - webkitdirectory is not in standard types */
        webkitdirectory="true"
        directory="true"
        multiple
        accept="application/pdf"
        onChange={handleFolderSelect}
        style={{ display: 'none' }}
      />

      {/* Select folder button */}
      <Button
        variant="outlined"
        fullWidth
        size="large"
        startIcon={<FolderOpen />}
        onClick={handleSelectClick}
        disabled={!activeSession || uploading}
        sx={{ mb: 2 }}
      >
        Select Folder
      </Button>

      {/* Show selected files info */}
      {selectedFiles.length > 0 && !uploading && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Folder: <strong>{folderName}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Files: {selectedFiles.length} PDF{selectedFiles.length !== 1 ? 's' : ''}
          </Typography>

          {/* Upload button */}
          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={<Upload />}
            onClick={handleUpload}
            sx={{ mt: 2 }}
          >
            Upload All PDFs
          </Button>
        </Box>
      )}

      {/* Upload progress */}
      {uploading && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Uploading and extracting PDFs...
          </Typography>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            {Math.round(uploadProgress)}% complete
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
